import {zodResolver} from '@hookform/resolvers/zod';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {Button} from '@/components/ui/button';
import type {DialogProps} from '@/components/ui/dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {Form} from '@/components/ui/form';
import {useToast} from '@/components/ui/use-toast';
import {graphqlClient} from '@/services/graphqlClient';
import {
  useCreateJobRoleMutation,
  useGetJobRolesQuery,
} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';

import type {JobRoleFormValues} from '../utils/jobRoleFormInputs';
import {
  jobRoleFormDefaultValues,
  jobRoleFormSchema,
  mapJobRoleFormValuesToCreateJobRoleInput,
} from '../utils/jobRoleFormInputs';
import JobRoleFormFields from './JobRoleFormFields';

export type AddJobRoleDialogProps = DialogProps;

const AddJobRoleDialog = ({onOpenChange, ...props}: AddJobRoleDialogProps) => {
  const {t} = useTranslation();
  const {toast} = useToast();

  const createJobRole = useCreateJobRoleMutation(graphqlClient, {
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: useGetJobRolesQuery.getKey()});
    },
  });

  const form = useForm<JobRoleFormValues>({
    resolver: zodResolver(jobRoleFormSchema),
    defaultValues: jobRoleFormDefaultValues(),
  });

  const handleSubmit = async (values: JobRoleFormValues) => {
    try {
      const response = await createJobRole.mutateAsync({
        input: mapJobRoleFormValuesToCreateJobRoleInput(values),
      });
      toast({
        title: t('Job role {{name}} added', {
          name: response?.createJobRole.name,
        }),
        variant: 'success',
      });
      form.reset(jobRoleFormDefaultValues());
      onOpenChange?.(true);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Form {...form}>
      <Dialog {...props} onOpenChange={onOpenChange}>
        <DialogContent asChild>
          <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
            <DialogHeader>
              <DialogTitle>{t('Add job role')}</DialogTitle>
            </DialogHeader>
            <DialogSection>
              <JobRoleFormFields form={form} />
            </DialogSection>
            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={() => onOpenChange?.(false)}
              >
                {t('Cancel')}
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={createJobRole.isPending}
              >
                {t('Add')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Form>
  );
};

export default AddJobRoleDialog;
